import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { dashboardApi, dashFilterExtra } from '../../services/dashboard-api.service';
import FilterBar from '../../components/dashboard-report/FilterBar';
import type { PendingTaskGroup, DashboardFilters, WorkDetailRow } from '../../types/dashboard.types';

const TASK_LABELS: Record<string, string> = {
  'ts-to-be-accorded': 'TS to be accorded',
  'tender-stage': 'Tender Stage',
  'agreements': 'Agreements to be concluded',
  'cr-approvals': 'CR Approvals Pending',
  'final-payment': 'Final Payment Pending',
  'pending-updation': 'Pending Updation of Physical & Financial Progress',
  'eoat': 'EOAT — Expired On Agreed Time',
  'forest-clearance': 'Works Held Up due to Forest Clearance',
};

const PendingTaskDetail: React.FC = () => {
  const { taskType } = useParams<{ taskType: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PendingTaskGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DashboardFilters>({ grant: 'All' });

  const title = TASK_LABELS[taskType || ''] || taskType || 'Pending Tasks';

  const loadData = useCallback(async () => {
    if (!taskType) return;
    try {
      setLoading(true);
      setError(null);
      const { grant, districtId, mandalId } = filters;
      const g = grant === 'All' ? undefined : grant;
      const result = await dashboardApi.getPendingTasksByType(taskType, g, districtId, mandalId, dashFilterExtra(filters));
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [taskType, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const allWorks = data.flatMap((g) => g.works);
  const totalWorks = data.reduce((s, g) => s + g.work_count, 0);
  const totalCost = data.reduce((s, g) => s + g.estimate_cost_lakhs, 0);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 rounded-md hover:bg-gray-200 text-gray-600"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500">{totalWorks} works | Est. Cost: {totalCost.toFixed(2)} Lakhs</p>
        </div>
      </div>

      {/* Filters */}
      <FilterBar filters={filters} onFiltersChange={setFilters} onRefresh={loadData} loading={loading} showSchemeType />

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800 text-sm">{error}</span>
          </div>
          <button onClick={loadData} className="text-red-600 hover:text-red-800 underline text-sm">Retry</button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
          <span className="text-gray-600">Loading...</span>
        </div>
      )}

      {!loading && (
        <>
          {/* Grant-wise Summary Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6 overflow-hidden">
            <div className="bg-blue-600 text-white px-4 py-2">
              <h3 className="text-sm font-semibold">Grant-wise Summary</h3>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Sno</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Grant</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Number of works</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Estimate cost (in Lakhs)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.map((g, i) => (
                  <tr key={g.grant_type} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{i + 1}</td>
                    <td className="px-4 py-2 font-medium">{g.grant_type}</td>
                    <td className="px-4 py-2 text-right">{g.work_count}</td>
                    <td className="px-4 py-2 text-right">{g.estimate_cost_lakhs.toFixed(2)}</td>
                  </tr>
                ))}
                {data.length > 0 && (
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-4 py-2" colSpan={2}>Total</td>
                    <td className="px-4 py-2 text-right">{totalWorks}</td>
                    <td className="px-4 py-2 text-right">{totalCost.toFixed(2)}</td>
                  </tr>
                )}
                {data.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No data available</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Work-wise Detail Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-700 text-white px-4 py-2">
              <h3 className="text-sm font-semibold">Work wise format</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    {[
                      'Sno', 'Work ID', 'District', 'Name of the work', 'Type of Work',
                      'Grant', 'Year of Sanction', 'Estimate Cost', 'Sanctioned Length',
                      'Cum. Expenditure', 'Completed Length', 'Date of Agmt.',
                      'Date set for completion', 'Stage of work', 'Sub Stage', 'Detailed Remarks',
                    ].map((h) => (
                      <th key={h} className="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap border-b">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {allWorks.map((w: WorkDetailRow, idx: number) => (
                    <tr key={`${w.work_id}-${idx}`} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5">{idx + 1}</td>
                      <td className="px-2 py-1.5 font-mono text-blue-600">{w.work_id}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{w.district_name}</td>
                      <td className="px-2 py-1.5 max-w-[250px] truncate" title={w.work_name}>{w.work_name}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{w.work_type || '-'}</td>
                      <td className="px-2 py-1.5">{w.grant_type}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{w.oas_sanction_date || '-'}</td>
                      <td className="px-2 py-1.5 text-right">{w.estimate_cost.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">{w.length_sanctioned_km}</td>
                      <td className="px-2 py-1.5 text-right">{w.cum_exp_on_work.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">{w.cumulative_length_completed}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{w.agreement_date || '-'}</td>
                      <td className={`px-2 py-1.5 whitespace-nowrap ${taskType === 'eoat' ? 'text-red-600 font-medium' : ''}`}>
                        {w.completion_date_set || '-'}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{w.abstract_stage_of_work}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{w.sub_stage_of_work || '-'}</td>
                      <td className="px-2 py-1.5 max-w-[150px] truncate" title={w.detailed_stage_of_work}>
                        {w.detailed_stage_of_work || '-'}
                      </td>
                    </tr>
                  ))}
                  {allWorks.length === 0 && (
                    <tr><td colSpan={16} className="px-4 py-8 text-center text-gray-400">No works found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PendingTaskDetail;
